import React, { useState, useEffect } from 'react';
import { 
  FileJson, 
  Upload, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Info,
  Server as ServerIcon,
  AlertTriangle
} from 'lucide-react';
import { apiService } from '../services/api';
import { ImportResponse } from '../types/api';
import { ValidationResponse } from '../services/api/import.service';

interface ImportServer {
  id: number;
  name: string;
  description?: string;
}

const DataImportImproved: React.FC = () => {
  const [servers, setServers] = useState<ImportServer[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResponse | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showValidationDetails, setShowValidationDetails] = useState(false);

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      const response = await apiService.getServers();
      setServers(response);
    } catch (error) {
      console.error('Error fetching servers:', error);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
      
      // Automatically validate the file
      if (file.name.endsWith('.json')) {
        await validateFile(file);
      }
    }
  };

  const validateFile = async (file: File) => {
    setIsLoading(true);
    try {
      const result = await apiService.import.validateFile(
        file, 
        selectedServerId ? parseInt(selectedServerId) : undefined
      );
      setValidationResult(result);
      setShowValidationDetails(true);
    } catch (error) {
      console.error('Validation error:', error);
      setValidationResult({
        valid: false,
        errors: ['Failed to validate file'],
        warnings: [],
        missing_server_info: false,
        summary: 'Validation failed',
        validation_details: 'An error occurred while validating the file.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportFile = async () => {
    if (!selectedFile || !selectedServerId) {
      alert('Please select a file and server');
      return;
    }

    setIsLoading(true);
    setImportResult(null);

    try {
      const response = await apiService.import.importFile(
        selectedFile,
        parseInt(selectedServerId)
      );
      setImportResult(response);
      setValidationResult(null);
    } catch (error: any) {
      const errorResponse = error.response?.data || {};
      setImportResult({
        success: false,
        message: errorResponse.message || 'Import failed',
        errors: errorResponse.errors || ['Unknown error occurred'],
        warnings: errorResponse.warnings,
        validation_details: errorResponse.validation_details
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportPredefined = async (type: 'supervisor' | 'controller') => {
    if (!selectedServerId) {
      alert('Please select a server first');
      return;
    }

    setIsLoading(true);
    setImportResult(null);
    setValidationResult(null);

    try {
      const response = type === 'supervisor' 
        ? await apiService.importSupervisorJson(parseInt(selectedServerId))
        : await apiService.importControllerJson(parseInt(selectedServerId));
      
      setImportResult(response);
    } catch (error: any) {
      const errorResponse = error.response?.data || {};
      setImportResult({
        success: false,
        message: errorResponse.message || `Failed to import ${type}.json`,
        errors: errorResponse.errors || ['Unknown error occurred'],
        warnings: errorResponse.warnings,
        validation_details: errorResponse.validation_details
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderValidationResult = () => {
    if (!validationResult) return null;

    return (
      <div className={`mt-4 p-4 rounded-lg border ${
        validationResult.valid 
          ? 'bg-green-50 border-green-200' 
          : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-start space-x-3">
          {validationResult.valid ? (
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          ) : (
            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
          )}
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">
              {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
            </h4>
            <p className="text-sm text-gray-600 mt-1">{validationResult.summary}</p>
            
            {validationResult.errors.length > 0 && (
              <div className="mt-3">
                <h5 className="text-sm font-semibold text-red-700">Errors:</h5>
                <ul className="mt-1 space-y-1">
                  {validationResult.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-600 flex items-start">
                      <span className="mr-2">•</span>
                      <span>{error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {validationResult.warnings.length > 0 && (
              <div className="mt-3">
                <h5 className="text-sm font-semibold text-yellow-700">Warnings:</h5>
                <ul className="mt-1 space-y-1">
                  {validationResult.warnings.map((warning, index) => (
                    <li key={index} className="text-sm text-yellow-600 flex items-start">
                      <span className="mr-2">•</span>
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {validationResult.missing_server_info && (
              <div className="mt-3 p-2 bg-blue-100 rounded">
                <p className="text-sm text-blue-800">
                  <Info className="inline w-4 h-4 mr-1" />
                  Server selection is required for import
                </p>
              </div>
            )}
            
            {validationResult.validation_details && (
              <div className="mt-3">
                <button
                  onClick={() => setShowValidationDetails(!showValidationDetails)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showValidationDetails ? 'Hide' : 'Show'} Details
                </button>
                {showValidationDetails && (
                  <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
                    {validationResult.validation_details}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderImportResult = () => {
    if (!importResult) return null;

    return (
      <div className={`mt-4 p-4 rounded-lg border ${
        importResult.success 
          ? 'bg-green-50 border-green-200' 
          : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-start space-x-3">
          {importResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          ) : (
            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
          )}
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">
              {importResult.success ? 'Import Successful' : 'Import Failed'}
            </h4>
            <p className="text-sm text-gray-600 mt-1">{importResult.message}</p>
            
            {importResult.imported_counts && (
              <div className="mt-3">
                <h5 className="text-sm font-semibold text-gray-700">Imported Records:</h5>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {Object.entries(importResult.imported_counts).map(([key, value]) => (
                    value > 0 && (
                      <div key={key} className="text-sm text-gray-600">
                        <span className="capitalize">{key}:</span> {value}
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
            
            {importResult.updated_counts && (
              <div className="mt-3">
                <h5 className="text-sm font-semibold text-gray-700">Updated Records:</h5>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {Object.entries(importResult.updated_counts).map(([key, value]) => (
                    value > 0 && (
                      <div key={key} className="text-sm text-gray-600">
                        <span className="capitalize">{key}:</span> {value}
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
            
            {importResult.warnings && importResult.warnings.length > 0 && (
              <div className="mt-3">
                <h5 className="text-sm font-semibold text-yellow-700">Warnings:</h5>
                <ul className="mt-1 space-y-1">
                  {importResult.warnings.map((warning, index) => (
                    <li key={index} className="text-sm text-yellow-600 flex items-start">
                      <AlertTriangle className="w-4 h-4 mr-1 mt-0.5" />
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {importResult.errors && importResult.errors.length > 0 && (
              <div className="mt-3">
                <h5 className="text-sm font-semibold text-red-700">Errors:</h5>
                <ul className="mt-1 space-y-1">
                  {importResult.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-600 flex items-start">
                      <span className="mr-2">���</span>
                      <span>{error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {importResult.validation_details && (
              <div className="mt-3">
                <details className="cursor-pointer">
                  <summary className="text-sm text-blue-600 hover:text-blue-800">
                    View Validation Details
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
                    {importResult.validation_details}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Data Import with Validation</h1>
      
      {/* Server Selection */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ServerIcon className="w-5 h-5 mr-2" />
          Server Selection
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Target Server <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedServerId}
              onChange={(e) => {
                setSelectedServerId(e.target.value);
                // Re-validate file if one is selected
                if (selectedFile && e.target.value) {
                  validateFile(selectedFile);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              <option value="">-- Select a server --</option>
              {servers.map((server) => (
                <option key={server.id} value={server.id}>
                  {server.name} {server.description && `- ${server.description}`}
                </option>
              ))}
            </select>
            {!selectedServerId && (
              <p className="mt-1 text-sm text-yellow-600">
                <AlertCircle className="inline w-4 h-4 mr-1" />
                Server selection is required for import
              </p>
            )}
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Upload className="w-5 h-5 mr-2" />
          Upload JSON File
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
              disabled={isLoading}
            />
            <p className="mt-1 text-sm text-gray-500">
              Upload supervisor.json or controller.json files
            </p>
          </div>
          
          {selectedFile && (
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-700">
                <FileJson className="inline w-4 h-4 mr-1" />
                Selected: {selectedFile.name}
              </p>
            </div>
          )}
          
          {renderValidationResult()}
          
          <button
            onClick={handleImportFile}
            disabled={!selectedFile || !selectedServerId || isLoading || (validationResult !== null && !validationResult.valid)}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Processing...' : 'Import File'}
          </button>
        </div>
      </div>

      {/* Predefined Imports */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileJson className="w-5 h-5 mr-2" />
          Import Predefined Files
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => handleImportPredefined('controller')}
            disabled={!selectedServerId || isLoading}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            <h3 className="font-semibold text-gray-900">Controller Data</h3>
            <p className="text-sm text-gray-600 mt-1">Import from controller.json</p>
          </button>
          
          <button
            onClick={() => handleImportPredefined('supervisor')}
            disabled={!selectedServerId || isLoading}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            <h3 className="font-semibold text-gray-900">Supervisor Data</h3>
            <p className="text-sm text-gray-600 mt-1">Import from supervisor.json</p>
          </button>
        </div>
      </div>

      {/* Import Result */}
      {renderImportResult()}
      
      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
          <Info className="w-5 h-5 mr-2" />
          Import Information
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Files are validated before import to ensure all required fields are present</li>
          <li>• Server selection is mandatory to avoid import errors</li>
          <li>• Warnings indicate optional fields that could improve data quality</li>
          <li>• Existing records will be updated if they match by ID</li>
        </ul>
      </div>
    </div>
  );
};

export default DataImportImproved;
